import { computed, Injectable, inject, signal } from "@angular/core";
import { NavigationEnd } from "@angular/router";
import { IApiResponse } from "@models/api-response.model";
import { Country } from "@models/country.model";

import { ICategory } from "@models/product-list.model";
import { ISessionData, IUser } from "@models/user.model";
import { format, parseISO } from "date-fns";
import { BehaviorSubject, catchError, map, Observable, of } from "rxjs";
import { useSubject } from "../../utils";
import { RequestService } from "../request/request.service";

@Injectable({
  providedIn: "root",
})
export class AppStateService {
  private requestService = inject(RequestService);

  windowSize$ = new BehaviorSubject<{
    innerWidth: number;
    innerHeight: number;
  }>(null as any);
  deviceType$ = new BehaviorSubject<"desktop" | "mobile">(null as any);
  routeNavigationEnd$ = useSubject<NavigationEnd>(null as any);
  // Non-empty once a session is established (holds the user id as a string) — kept as a
  // string/BehaviorSubject for compatibility with existing `!!token` truthiness checks
  // (AuthGaurd, main-nav sidenav visibility) even though there's no real bearer token
  // anymore; the actual session lives in the httpOnly `mb_session` cookie.
  userToken$ = new BehaviorSubject<string>("");
  user$ = new BehaviorSubject<IUser>({ name: "", email: "", profile_pic: "" });
  currentUserId = signal<number | null>(null);
  // Shared loading flag for every fh-refresh-button instance across the app.
  isRefreshing = signal<boolean>(false);
  parentCategories = signal<ICategory[]>([]);
  allCountries = signal<Country[]>([]);
  /** The signed-in merchant's country (its products' supplier country), once known. */
  merchantIso2 = signal<string | null>(null);
  /**
   * Markets a merchant can list in: open for listing or live (admin portal >
   * Country > Market launch). A new market is open for listing before shoppers
   * can see it, so merchants can stock it ahead of launch.
   */
  destinationCountries = computed(() =>
    this.allCountries().filter((country) => country.listing_enabled || country.destination_enabled)
  );
  /** Live market codes, upper-case; SG and US until the country list has loaded. */
  liveMarketCodes = computed(() => {
    const codes = this.allCountries()
      .filter((country) => country.destination_enabled)
      .map((c) => c.iso2.toUpperCase());
    return codes.length ? codes : ["SG", "US"];
  });
  getFormattedDateFromISO({
    isoDateStr,
    dateFormat,
  }: {
    isoDateStr: string;
    dateFormat: "yyyy-MM-dd HH:mm a";
  }): string {
    return format(parseISO(isoDateStr), dateFormat);
  }
  apiLoading$ = new BehaviorSubject<boolean>(false);
  constructor() {
    this.apiLoading$.subscribe((loading) => {
      console.log("apiLoading$", loading);
    });
  }

  /**
   * Validates the httpOnly session cookie against the backend and hydrates auth state
   * from the result. Used for: app-bootstrap (main.ts APP_INITIALIZER), AuthGaurd's
   * live per-navigation check, and the shared refresh-button auth check below.
   */
  checkSession(): Observable<boolean> {
    return this.requestService.get<IApiResponse<ISessionData>>("/api/v1/auth/session").pipe(
      map((res) => {
        if (res.status === true) {
          this.setUserSession(res.data);
          return true;
        }
        this.clearUserSession();
        return false;
      }),
      catchError(() => {
        this.clearUserSession();
        return of(false);
      })
    );
  }

  /** Alias used by the app-bootstrap APP_INITIALIZER — same check as checkSession(). */
  bootstrapSession(): Observable<boolean> {
    return this.checkSession();
  }

  /** Hydrates auth state directly from a session-shaped response (login/google-login). */
  setUserSession(data: ISessionData): void {
    this.userToken$.next(String(data.id));
    this.currentUserId.set(data.id);
    // Login responses do not carry it; the session check that follows does.
    if (data.iso2 !== undefined) this.merchantIso2.set(data.iso2 ? data.iso2.toUpperCase() : null);
    this.user$.next({
      name: data.name,
      email: data.email,
      profile_pic: data.profile_pic || "",
    });
  }

  clearUserSession(): void {
    this.userToken$.next("");
    this.currentUserId.set(null);
    this.merchantIso2.set(null);
    this.user$.next({ name: "", email: "", profile_pic: "" });
  }

  /** Updates just the avatar after a successful profile-picture upload — no reload needed. */
  updateProfilePic(profilePic: string): void {
    this.user$.next({ ...this.user$.value, profile_pic: profilePic });
  }

  /**
   * Shared no-reload refresh: re-validates the session, then either invokes the page's
   * own refetch method (still logged in) or hard-redirects to /login (session gone).
   * Wired up by fh-refresh-button on every page.
   */
  refreshCurrentView(refetchFn: () => void): void {
    if (this.isRefreshing()) return;
    this.isRefreshing.set(true);
    this.checkSession().subscribe({
      next: (isAuth) => {
        this.isRefreshing.set(false);
        if (isAuth) {
          refetchFn();
        } else {
          window.location.href = "/login";
        }
      },
      error: () => {
        this.isRefreshing.set(false);
        window.location.href = "/login";
      },
    });
  }
}
